import {MouseEvent} from "react";
import {css} from "@panda/css";
import {motion} from "framer-motion";

import {Button} from "@pg-design/button-module";
import {HeartFillIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {PingPongOverflowText} from "@pg-mono/ping-pong-overflow-text";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {handleFavouritesListCheckHit} from "../../tracking/algolytics_hits/favourites_list_hit";
import {useFavourites} from "../hooks/use_favourites";
import {hitGtmAddToCompareClick} from "../tracking/favourite_gtm_events";
import {AmplitudeFavouriteLocation} from "../tracking/get_amplitude_favourites_event_properties";
import {trackAmplitudeRemoveFromFavourites} from "../tracking/track_amplitude_remove_from_favourites";
import {IFavouritesTopbarCard} from "../utils/map_favourite_to_topbar_card";

interface IProps {
    card: IFavouritesTopbarCard;
    index: number;
    location: AmplitudeFavouriteLocation;
    onDeleteCancel: (propertyId: number) => void;
    onDeleteStart: (propertyId: number) => void;
    onOverlayExitComplete?: () => void;
    overlayTop?: number;
}

const LIST_ITEM_TRANSITION = {
    duration: 0.45,
    ease: "easeInOut"
};

const trackFavouritesDropdownSelectItem = (card: IFavouritesTopbarCard, index: number) => {
    trackAmplitudeEvent("select_item", {
        location: "rp_favourites_dropdown",
        item_list_name: "Ulubione",
        index,
        property_id: card.propertyId,
        item_id: card.offerId,
        item_name: card.name
    });
};

export const FavouritesTopbarCard = (props: IProps) => {
    const {card, index, location, onDeleteCancel, onDeleteStart, onOverlayExitComplete, overlayTop} = props;
    const {removePropertyFromFavourites} = useFavourites();
    const href = card.href;
    const isOverlay = overlayTop != null;

    const handleSelect = () => {
        trackFavouritesDropdownSelectItem(card, index);
    };

    const handleRemove = async (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onDeleteStart(card.propertyId);

        const wasRemoved = await removePropertyFromFavourites(card.propertyId);

        if (!wasRemoved) {
            onDeleteCancel(card.propertyId);
            return;
        }

        if (card.offerId != null) {
            trackAmplitudeRemoveFromFavourites({
                location,
                favouritePlacement: "rp_favourite_dropdown",
                offer: {id: card.offerId, name: card.name},
                property: {id: card.propertyId}
            });
            handleFavouritesListCheckHit(false, card.offerId, card.propertyId);
        }

        hitGtmAddToCompareClick(false);
    };

    const cardBody = (
        <>
            {card.imageUrl ? <img alt="" className={cardImageStyle} src={card.imageUrl} /> : <div className={cardImageFallbackStyle} />}
            <div className={cardCopyStyle}>
                <PingPongOverflowText>
                    <Text as="span" noWrap variant="info_txt_2">
                        {card.name}
                    </Text>
                </PingPongOverflowText>
                {(card.priceLabel || card.priceM2Label) && (
                    <p className={cardPriceRowStyle}>
                        {card.priceLabel && (
                            <Text as="span" className={cardPriceStyle} variant="body_copy_2">
                                {card.priceLabel}
                            </Text>
                        )}
                        {card.priceM2Label && (
                            <Text as="span" color="var(--colors-gray-800)" variant="info_txt_2">
                                {card.priceM2Label}
                            </Text>
                        )}
                    </p>
                )}
                {card.metaLabel && (
                    <Text as="p" color="var(--colors-gray-800)" variant="info_txt_2">
                        {card.metaLabel}
                    </Text>
                )}
            </div>
        </>
    );

    return (
        <motion.li
            animate={isOverlay ? {opacity: 0, x: "100%"} : {opacity: 1, x: 0, y: 0}}
            className={cardItemStyle}
            data-favourite-id={card.propertyId}
            initial={isOverlay ? {opacity: 1, x: 0} : {opacity: 0, y: 12}}
            layout={!isOverlay}
            onAnimationComplete={isOverlay ? onOverlayExitComplete : undefined}
            style={isOverlay ? {left: 0, pointerEvents: "none", position: "absolute", top: overlayTop, width: "100%"} : undefined}
            transition={isOverlay ? {duration: 1, ease: "easeInOut"} : LIST_ITEM_TRANSITION}
        >
            <div className={cardStyle}>
                {href ? (
                    <a className={cardLinkStyle} data-testid={isOverlay ? undefined : NAVIGATION_TEST_IDS.FAVOURITES.CARD} href={href} onClick={handleSelect}>
                        {cardBody}
                    </a>
                ) : (
                    <div className={cardLinkStyle} data-testid={isOverlay ? undefined : NAVIGATION_TEST_IDS.FAVOURITES.CARD}>
                        {cardBody}
                    </div>
                )}
                <Button
                    aria-label="Usuń z ulubionych"
                    className={heartButtonStyle}
                    contentClassName={heartContentStyle}
                    dataTestId={isOverlay ? undefined : NAVIGATION_TEST_IDS.FAVOURITES.CARD_REMOVE}
                    iconLeft={HeartFillIcon}
                    iconLeftProps={{
                        fill: "var(--colors-secondary)",
                        size: "2",
                        wrapperColor: "transparent",
                        wrapperSize: "2"
                    }}
                    onClick={isOverlay ? undefined : handleRemove}
                    onMouseDown={(event) => event.stopPropagation()}
                    type="button"
                    variant="transparent"
                />
            </div>
        </motion.li>
    );
};

const cardItemStyle = css({
    position: "relative",
    w: "100%"
});

const cardStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1,
    p: 0.5,
    pr: 1.5,
    borderTop: "1px solid var(--colors-gray-300)",
    borderRadius: "1.2rem"
});

const cardLinkStyle = css({
    display: "flex",
    alignItems: "center",
    flex: 1,
    gap: 1,
    minW: 0,
    color: "inherit"
});

const cardImageStyle = css({
    flex: "0 0 auto",
    w: "8.2rem",
    h: "6.1rem",
    objectFit: "cover",
    borderRadius: 1
});

const cardImageFallbackStyle = css({
    flex: "0 0 auto",
    w: "8.2rem",
    h: "6.1rem",
    bg: "var(--colors-gray-200)",
    borderRadius: 1
});

const cardCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    minW: 0
});

const cardPriceRowStyle = css({
    display: "flex",
    alignItems: "baseline",
    gap: 1,
    m: 0
});

const cardPriceStyle = css({
    fontWeight: 500
});

const heartButtonStyle = css({
    flex: "0 0 auto",
    minWidth: "unset",
    w: "2rem",
    h: "2rem",
    p: 0
});

const heartContentStyle = css({
    "& > span:empty": {
        display: "none"
    }
});
import {MouseEvent} from "react";
import {css} from "@panda/css";
import {motion} from "framer-motion";

import {Button} from "@pg-design/button-module";
import {HeartFillIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {PingPongOverflowText} from "@pg-mono/ping-pong-overflow-text";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {handleFavouritesListCheckHit} from "../../tracking/algolytics_hits/favourites_list_hit";
import {useFavourites} from "../hooks/use_favourites";
import {hitGtmAddToCompareClick} from "../tracking/favourite_gtm_events";
import {AmplitudeFavouriteLocation} from "../tracking/get_amplitude_favourites_event_properties";
import {trackAmplitudeRemoveFromFavourites} from "../tracking/track_amplitude_remove_from_favourites";
import {IFavouritesTopbarCard} from "../utils/map_favourite_to_topbar_card";

interface IProps {
    card: IFavouritesTopbarCard;
    index: number;
    location: AmplitudeFavouriteLocation;
    onDeleteCancel: (propertyId: number) => void;
    onDeleteStart: (propertyId: number) => void;
    onOverlayExitComplete?: () => void;
    overlayTop?: number;
}

const LIST_ITEM_TRANSITION = {
    duration: 0.45,
    ease: "easeInOut"
};

const trackFavouritesDropdownSelectItem = (card: IFavouritesTopbarCard, index: number) => {
    trackAmplitudeEvent("select_item", {
        location: "rp_favourites_dropdown",
        item_list_name: "Ulubione",
        index,
        property_id: card.propertyId,
        item_id: card.offerId,
        item_name: card.name
    });
};

export const FavouritesTopbarCard = (props: IProps) => {
    const {card, index, location, onDeleteCancel, onDeleteStart, onOverlayExitComplete, overlayTop} = props;
    const {removePropertyFromFavourites} = useFavourites();
    const href = card.href;
    const isOverlay = overlayTop != null;

    const handleSelect = () => {
        trackFavouritesDropdownSelectItem(card, index);
    };

    const handleRemove = async (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onDeleteStart(card.propertyId);

        const wasRemoved = await removePropertyFromFavourites(card.propertyId);

        if (!wasRemoved) {
            onDeleteCancel(card.propertyId);
            return;
        }

        if (card.offerId != null) {
            trackAmplitudeRemoveFromFavourites({
                location,
                favouritePlacement: "rp_favourite_dropdown",
                offer: {id: card.offerId, name: card.name},
                property: {id: card.propertyId}
            });
            handleFavouritesListCheckHit(false, card.offerId, card.propertyId);
        }

        hitGtmAddToCompareClick(false);
    };

    const cardBody = (
        <>
            {card.imageUrl ? <img alt="" className={cardImageStyle} src={card.imageUrl} /> : <div className={cardImageFallbackStyle} />}
            <div className={cardCopyStyle}>
                <PingPongOverflowText>
                    <Text as="span" noWrap variant="info_txt_2">
                        {card.name}
                    </Text>
                </PingPongOverflowText>
                {(card.priceLabel || card.priceM2Label) && (
                    <p className={cardPriceRowStyle}>
                        {card.priceLabel && (
                            <Text as="span" className={cardPriceStyle} variant="body_copy_2">
                                {card.priceLabel}
                            </Text>
                        )}
                        {card.priceM2Label && (
                            <Text as="span" color="var(--colors-gray-800)" variant="info_txt_2">
                                {card.priceM2Label}
                            </Text>
                        )}
                    </p>
                )}
                {card.metaLabel && (
                    <Text as="p" color="var(--colors-gray-800)" variant="info_txt_2">
                        {card.metaLabel}
                    </Text>
                )}
            </div>
        </>
    );

    return (
        <motion.li
            animate={isOverlay ? {opacity: 0, x: "100%"} : {opacity: 1, x: 0, y: 0}}
            className={cardItemStyle}
            data-favourite-id={card.propertyId}
            initial={isOverlay ? {opacity: 1, x: 0} : {opacity: 0, y: 12}}
            layout={!isOverlay}
            onAnimationComplete={isOverlay ? onOverlayExitComplete : undefined}
            style={isOverlay ? {left: 0, pointerEvents: "none", position: "absolute", top: overlayTop, width: "100%"} : undefined}
            transition={isOverlay ? {duration: 1, ease: "easeInOut"} : LIST_ITEM_TRANSITION}
        >
            <div className={cardStyle}>
                {href ? (
                    <a className={cardLinkStyle} data-testid={isOverlay ? undefined : NAVIGATION_TEST_IDS.FAVOURITES.CARD} href={href} onClick={handleSelect}>
                        {cardBody}
                    </a>
                ) : (
                    <div className={cardLinkStyle} data-testid={isOverlay ? undefined : NAVIGATION_TEST_IDS.FAVOURITES.CARD}>
                        {cardBody}
                    </div>
                )}
                <Button
                    aria-label="Usuń z ulubionych"
                    className={heartButtonStyle}
                    contentClassName={heartContentStyle}
                    dataTestId={isOverlay ? undefined : NAVIGATION_TEST_IDS.FAVOURITES.CARD_REMOVE}
                    iconLeft={HeartFillIcon}
                    iconLeftProps={{
                        fill: "var(--colors-secondary)",
                        size: "2",
                        wrapperColor: "transparent",
                        wrapperSize: "2"
                    }}
                    onClick={isOverlay ? undefined : handleRemove}
                    onMouseDown={(event) => event.stopPropagation()}
                    type="button"
                    variant="transparent"
                />
            </div>
        </motion.li>
    );
};

const cardItemStyle = css({
    position: "relative",
    w: "100%"
});

const cardStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1,
    p: 0.5,
    pr: 1.5,
    borderTop: "1px solid var(--colors-gray-300)",
    borderRadius: "1.2rem"
});

const cardLinkStyle = css({
    display: "flex",
    alignItems: "center",
    flex: 1,
    gap: 1,
    minW: 0,
    color: "inherit"
});

const cardImageStyle = css({
    flex: "0 0 auto",
    w: "8.2rem",
    h: "6.1rem",
    objectFit: "cover",
    borderRadius: 1
});

const cardImageFallbackStyle = css({
    flex: "0 0 auto",
    w: "8.2rem",
    h: "6.1rem",
    bg: "var(--colors-gray-200)",
    borderRadius: 1
});

const cardCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    minW: 0
});

const cardPriceRowStyle = css({
    display: "flex",
    alignItems: "baseline",
    gap: 1,
    m: 0
});

const cardPriceStyle = css({
    fontWeight: 500
});

const heartButtonStyle = css({
    flex: "0 0 auto",
    minWidth: "unset",
    w: "2rem",
    h: "2rem",
    p: 0
});

const heartContentStyle = css({
    "& > span:empty": {
        display: "none"
    }
});
